import { Fragment, useEffect } from 'react';
import { useFormikContext } from 'formik';

import { Select } from '../../Form/Select/Select';
import { capitalizeFirstLetter } from '../../../utils';
import { usePreviousValue } from '../../../hooks/usePreviousValue';
import { ComponentContext } from '../../../contexts/ComponentContext';
import { PresetPicker } from '../../PresetPicker/PresetPicker';
import { componentsConfiguration } from '../configuration';
import { StayliveChannelsSelect } from '../../Form/StayliveChannelsSelect/StayliveChannelsSelect';
import { getProvider } from '../../../services/svpProvider';

import { EndpointParamsPicker } from './EndpointParamsPicker/EndpointParamsPicker';
import { DataSourceModeSwitch } from './DataSourceModeSwitch/DataSourceModeSwitch';
import { resolveDataSourceMode } from './resolveDataSourceMode';

import type { ReactNode } from 'react';
import type { ComponentResponse, DataEndpoint } from '../../../types';

type DeprecationNoticeProps = {
  oldValue: string;
  newValue: string;
};

const DeprecationNotice = ({ oldValue, newValue }: DeprecationNoticeProps) => {
  return (
    <>
      <s>{oldValue}</s> (use {newValue} instead)
    </>
  );
};

const deprecatedEndpoints: Partial<Record<DataEndpoint, string>> = {
  'svp/assets': 'svp/search',
  'svp/most-seen': 'svp/search',
};

type EndpointOption = {
  value: string;
  label: ReactNode;
};

type Props = {
  inputsSize: string;
};

function DataSource({ inputsSize }: Props) {
  const { values: component, setFieldValue } =
    useFormikContext<ComponentResponse>();
  const provider = getProvider();

  const componentConfig =
    componentsConfiguration.dataSources[
      component.type as keyof typeof componentsConfiguration.dataSources
    ];

  const configuration = componentConfig
    ? (
        componentConfig as ReadonlyArray<{
          dataType: string;
          endpoints?: readonly string[];
          preset?: boolean;
        }>
      ).filter((dataSource) => {
        if (dataSource.dataType === 'staylive/streams' && provider !== 'ab') {
          return false;
        }

        if (
          dataSource.dataType === 'stories' &&
          provider !== 'vgtv' /*, 'ab'*/
        ) {
          return false;
        }

        return true;
      })
    : undefined;

  const data = component?.data;
  const dataType = data?.type ?? null;
  const dataPreset = data?.preset ?? [];
  const dataSource =
    data?.source ?? ({} as ComponentResponse['data']['source']);
  const dataSourceEndpoint = dataSource?.endpoint;

  const dataSourceConfiguration =
    dataType && configuration
      ? configuration.find((config) => config.dataType === dataType)
      : null;

  const hasEndpoints = Boolean(dataSourceConfiguration?.endpoints?.length);
  const hasPreset = Boolean(dataSourceConfiguration?.preset);
  const mode = resolveDataSourceMode({
    hasEndpoints,
    hasPreset,
    storedMode: data?.dataSourceMode,
    hasPresetItems: dataPreset.length > 0,
  });

  const previousEndpoint = usePreviousValue(dataSourceEndpoint);
  const previousDataType = usePreviousValue(dataType);

  // clear the params list when endpoint is changed
  useEffect(() => {
    if (previousEndpoint && dataSourceEndpoint !== previousEndpoint) {
      setFieldValue('data.source.params', {});
    }
  }, [setFieldValue, previousEndpoint, dataSourceEndpoint]);

  useEffect(() => {
    if (previousDataType && dataType !== previousDataType) {
      // C8: clear the resolved preset virtual and preset2 when data type has changed
      setFieldValue('data.preset', undefined);
      setFieldValue('data.preset2', undefined);
      // C-DSM: clear the stored mode so the new data type re-derives its
      // default mode (preset-only / endpoint-only / dual-mode fallback)
      // instead of inheriting the previous data type's mode.
      setFieldValue('data.dataSourceMode', undefined);
      // C8: also clear params so stale params from the previous data type do
      // not survive into the new type (the endpoint-change effect fires only
      // when the endpoint *string* changes, so two types sharing the same
      // first endpoint would keep stale params without this explicit clear).
      setFieldValue('data.source.params', undefined);
      // set the first available endpoint for given data type, or unset the
      // entire source when the new data type has no endpoints (e.g. seasons)
      if (
        dataSourceConfiguration?.endpoints &&
        dataSourceConfiguration.endpoints.length > 0
      ) {
        setFieldValue(
          'data.source.endpoint',
          dataSourceConfiguration.endpoints[0]
        );
      } else {
        // C8: no endpoints for this data type — unset source entirely rather
        // than leaving the {endpoint: undefined, params: {}} husk behind.
        setFieldValue('data.source', undefined);
      }
    }
  }, [setFieldValue, previousDataType, dataType, dataSourceConfiguration]);

  return !configuration ? null : (
    <ComponentContext.Provider value={{ component }}>
      <Select
        label="Data type"
        name="data.type"
        options={configuration.map(({ dataType: dt }) => ({
          value: dt,
          label: capitalizeFirstLetter(dt),
        }))}
        size={inputsSize as Parameters<typeof Select>[0]['size']}
        allowEmpty={false}
      />
      {hasEndpoints && hasPreset && (
        <DataSourceModeSwitch
          value={mode}
          onChange={(newMode) => setFieldValue('data.dataSourceMode', newMode)}
        />
      )}
      {dataSourceConfiguration &&
        dataSourceConfiguration.endpoints &&
        mode === 'endpoint' && (
          <Fragment>
            <Select
              label="Data source"
              name="data.source.endpoint"
              size={inputsSize as Parameters<typeof Select>[0]['size']}
              options={
                dataSourceConfiguration.endpoints.map(
                  (endpoint): EndpointOption => ({
                    value: endpoint,
                    label:
                      endpoint in deprecatedEndpoints ? (
                        <DeprecationNotice
                          oldValue={endpoint}
                          newValue={
                            deprecatedEndpoints[endpoint as DataEndpoint]!
                          }
                        />
                      ) : (
                        endpoint
                      ),
                  })
                ) as unknown as Parameters<typeof Select>[0]['options']
              }
              allowEmpty={false}
            />
            {dataSource?.endpoint &&
              dataSource?.endpoint !== 'staylive/channel' && (
                <EndpointParamsPicker
                  dataType={dataType ?? ''}
                  endpoint={dataSource!.endpoint}
                  params={
                    dataSource?.params as Record<string, unknown> | undefined
                  }
                  onSubmit={(params) => {
                    setFieldValue('data.source.params', params);
                  }}
                />
              )}
            {dataSource?.endpoint === 'staylive/channel' && (
              <StayliveChannelsSelect
                label="Channel"
                name="data.source.params.channelId"
              />
            )}
          </Fragment>
        )}
      {dataSourceConfiguration &&
        dataSourceConfiguration.preset &&
        mode === 'preset' && (
          <PresetPicker
            dataType={dataType as ComponentResponse['data']['type']}
            presetItems={dataPreset}
            onChange={(preset) => setFieldValue('data.preset', preset)}
            singleItem={component.type === 'splash'}
          />
        )}
    </ComponentContext.Provider>
  );
}

export { DataSource };
